import _, { Dictionary } from 'lodash';
import * as constants from './constants';
import { Relationship, RelPoint, Field } from 'src/config';
import { ResourceComp } from 'src/config/index';
import { ENTITY } from '../types';

export type ID = string;

export type SimpleValue = string | number | boolean;

export interface Locator {
    name: string;
    id: ID;
}

export interface FetchRequest extends Locator { }

export type Value = SimpleValue | SimpleValue[] | ID | ID[]

export interface Error {
    message: string;
}

export type ErrorTree = Dictionary<Error[] | ErrorTree[]>;

export type Errors = Dictionary<Error[]>;

export interface Entity {
    attrs: Dictionary<Value>;
    values: Dictionary<Value>;
    errors: Errors;
    locked: boolean;
    morph?: string;
    full?: boolean;
    sys?: boolean;
}

export enum EntityType {
    Persistent = 'persistent',
    Transient = 'transient',
    Temp = 'temp',
    Root = 'root',
}

export type Entities = Dictionary<Dictionary<Entity>>;

export enum UpdateOpType {
    Replace = 'replace',
    Remove = 'remove',
    Add = 'add'
}

export interface UpdateOp {
    op: UpdateOpType;
    path: string;
    value?: any;
}

export interface StackItem {
    locator: Locator,
    vector?: RelPoint,
}

export interface Explorer {
    entities: Entities;
    stack: StackItem[];
}

export interface RelPort {
    relPoint: RelPoint;
    locator: Locator;
    attrName: string;
}

export interface BackRef {
    rel: Relationship;
    id: ID;
}

export type onCompleteHandler = (status: 'success' | 'error' | 'abort') => void;

// Actions

export interface StartFetchAction {
    type: typeof constants.START_FETCH;
    request: FetchRequest;
    full: boolean;
    force: boolean;
}

export interface SetEntityAction {
    type: typeof constants.SET_ENTITY;
    locator: Locator;
    entity: Entity;
}

export interface SetErrorsAction {
    type: typeof constants.SET_ERRORS;
    locator: Locator;
    errors: Errors;
}

export interface PushAction {
    type: typeof constants.PUSH;
    locator: Locator;
    vector: RelPoint;
}

export interface PopAction {
    type: typeof constants.POP;
}

export interface GotoAction {
    type: typeof constants.GOTO;
    index: number;
}

export interface ResetAction {
    type: typeof constants.RESET;
    locator: Locator;
}

export interface LinkAction {
    type: typeof constants.LINK;
    locator: Locator;
    vector: RelPoint;
    ids: ID[];
}

export interface UnLinkAction {
    type: typeof constants.UNLINK;
    locator: Locator;
    vector: RelPoint;
    ids: ID[];
}

export interface LinkHardAction {
    type: typeof constants.LINK_HARD;
    locator: Locator;
    vector: RelPoint;
    ids: ID[];
}

export interface UnLinkHardAction {
    type: typeof constants.UNLINK_HARD;
    locator: Locator;
    vector: RelPoint;
    ids: ID[];
}

export interface DeleteRelatedAction {
    type: typeof constants.DELETE_REL;
    locator: Locator;
    vector: RelPoint;
    ids: ID[];
}

export interface DeleteAction {
    type: typeof constants.DELETE;
    locator: Locator;
    onComplete?: onCompleteHandler;
}

export interface RemoveAction {
    type: typeof constants.REMOVE;
    locator: Locator;
}

export interface SubmitAction {
    type: typeof constants.SUBMIT;
    locator: Locator;
    onComplete?: onCompleteHandler;
}

export interface CancelAction {
    type: typeof constants.CANCEL;
    locator: Locator;
    onComplete?: onCompleteHandler;
}

export interface SetFieldValAction {
    type: typeof constants.SET_FIELD_VALUE;
    locator: Locator;
    field: Field;
    value: Value;
}

export interface SetVectorAction {
    type: typeof constants.SET_VECTOR_VALUE;
    locator: Locator;
    vector: RelPoint;
    ids: ID[];
    inclAttrs?: boolean; // set attrs
}

export interface SetMorphAction {
    type: typeof constants.SET_MORPH;
    locator: Locator;
    morph: string;
}

export interface SetShowHiddenAction {
    type: typeof constants.SET_SHOW_HIDDEN;
    locator: Locator;
    sys: boolean;
}

export interface ReplaceIdAction {
    type: typeof constants.REPLACE_ID;
    locator: Locator;
    id: ID;
}

export interface CreateTempAction {
    type: typeof constants.CREATE_TEMP;
    port: RelPort;
}

export interface CreateNewAction {
    type: typeof constants.CREATE_NEW;
    name: string;
}

export interface ClearAction {
    type: typeof constants.CLEAR;
}

export type Actions =
    StartFetchAction
    | SetEntityAction
    | SetErrorsAction
    | PushAction
    | PopAction
    | GotoAction
    | ResetAction
    | ClearAction
    | LinkAction
    | UnLinkAction
    | LinkHardAction
    | UnLinkHardAction
    | DeleteAction
    | RemoveAction
    | SubmitAction
    | CancelAction
    | SetFieldValAction
    | SetVectorAction

    | CreateTempAction
    | CreateNewAction

    | SetMorphAction
    | SetShowHiddenAction
    | ReplaceIdAction;

export interface EntityOptions {
    fields?: Dictionary<{ values?: string[] }>;
}
export interface EntityConf extends ResourceComp {
    type: typeof ENTITY;
    options?: EntityOptions;
}
